import {StyleSheet} from "aphrodite";
import {parsePath} from "history";
import * as React from "react";
// TODO: DIST-6497 - Revert back to react-router syntax once migrated to next-gen-admin
import {useMatch} from "react-router-dom-v5-compat";

import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {useLocation} from "@khan/urls";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedClientLink from "~/components/client-link-package/deprecated-client-link.tsx";

import {NavSubsectionContext} from "./nav-subsection-context.tsx";

interface Props {
    href: string;
    label: React.ReactNode;
    /* eslint-disable-next-line react/no-unused-prop-types --
       it is actually being used, but the eslint rule is confused because of how
       `PropsForStatelessComponent` uses `Props`. */
    handleIsCurrentPage?: (
        match: ReturnType<typeof useMatch>,
        location: ReturnType<typeof useLocation>,
    ) => boolean;
    onClick?: () => unknown;
    testId?: string;
}

// These extra props are provided automatically by the container component.
type PropsForStatelessComponent = Props & {
    // Whether this link points to the current page.
    isCurrentPage: boolean;
    // Whether this link is inside a <NavSubsection />.
    isInSubsection: boolean;
};

const BORDER_SIZE = 1;

/**
 * The core NavLink component. A simple wrapper for Wonder Blocks <Link />,
 * plus some styles and behaviors to fit the nav tree layout.
 *
 * This is actually the _stateless_ version of the component: rather than
 * detecting the current route in here, the <NavLink /> component provides an
 * `isCurrentPage` prop. This enables us to write fixture tests for the visuals
 * of this component, and check them separately from the routing integration.
 * (It also helps us avoid excess indentation from render-prop components!)
 */
export function StatelessNavLink({
    href,
    label,
    isCurrentPage,
    isInSubsection,
    testId,
    onClick,
}: PropsForStatelessComponent): React.ReactElement<React.ComponentProps<"li">> {
    // We are using ClientLink below instead of WonderBlocks Link since we need
    // to customize the styles for this navigation
    return (
        <li>
            <DeprecatedClientLink
                aria-current={
                    isCurrentPage ? ("page" as unknown as boolean) : false
                }
                to={href}
                testId={testId}
                onClick={onClick}
                style={[
                    styles.navLink,
                    isCurrentPage && styles.navLinkForCurrentPage,
                    isInSubsection && styles.navLinkInSubsection,
                ]}
            >
                {isCurrentPage ? (
                    <LabelLarge>{label}</LabelLarge>
                ) : (
                    <LabelMedium>{label}</LabelMedium>
                )}
            </DeprecatedClientLink>
        </li>
    );
}

/**
 * The NavLink container component. This wrapper provides stateful information
 * like `isCurrentPage` to the StatelessNavLink.
 *
 * TODO(mdr): Soon we'll also use context to provide the hierarchy level! Links
 *     inside a <NavSubsection /> should not be bold.
 */
function NavLink(props: Props): React.ReactElement {
    const {handleIsCurrentPage} = props;
    const location = useLocation();
    const parsedHref = parsePath(props.href);
    const match = useMatch(parsedHref.pathname);

    const isCurrentPage = () => {
        if (handleIsCurrentPage != null) {
            return handleIsCurrentPage(match, location);
        }
        return !!match;
    };

    return (
        <NavSubsectionContext.Consumer>
            {({isInSubsection}) => (
                <StatelessNavLink
                    {...props}
                    isCurrentPage={isCurrentPage()}
                    isInSubsection={isInSubsection}
                />
            )}
        </NavSubsectionContext.Consumer>
    );
}

const styles = StyleSheet.create({
    navLink: {
        color: semanticColor.core.foreground.neutral.strong,
        borderRadius: 4,
        paddingBlockStart: spacing.small_12 - BORDER_SIZE,
        paddingBlockEnd: spacing.small_12 - BORDER_SIZE,
        paddingInlineStart: spacing.large_24,
        border: `${BORDER_SIZE}px solid transparent`,
        display: "block",
        cursor: "pointer",
        ":focus": {
            borderColor: semanticColor.core.foreground.instructive.subtle,
            outline: 0,
        },
        ":hover": {
            textDecoration: "none",
            color: semanticColor.core.foreground.neutral.subtle,
        },
    },
    navLinkForCurrentPage: {
        backgroundColor: semanticColor.core.background.base.subtle,
        color: semanticColor.core.foreground.instructive.subtle,
        ":hover": {
            color: semanticColor.core.foreground.instructive.subtle,
        },
    },
    navLinkInSubsection: {
        paddingInlineStart: spacing.xLarge_32,
    },
});

export default NavLink;
